import {
  FormSelectShippingType,
  ShippingMethodCheckoutType,
} from '@/components/sections/builder/elements/body/checkout-form/component/checkout-form';
import ModalShippingCheckout from '@/components/sections/builder/elements/body/checkout-form/component/modal-select-shipping';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { useLocationMultiOrigin } from '@/hooks/api/storefront/menu-list';
import useModal from '@/hooks/useModal';
import { cn, formatEstimateDelivery, formatIDR } from '@/lib/utils';
import { HttpTypes } from '@/types';
import { BaseShippingRatesPayload } from '@/types/http/orders/common';
import { ChevronRight, ShieldCheck, TruckIcon } from 'lucide-react';
import Image from 'next/image';
import React from 'react';
import Cookies from 'js-cookie';
import { useCookies } from '@/app/cookie-provider';

type CardShippingCheckoutType = {
  carts: HttpTypes.StoreCartDetail;
  dataRates: BaseShippingRatesPayload;
  disabled?: boolean;
  isLoading?: boolean;
  hasNoPinpoint?: boolean;
  setIsCod: React.Dispatch<React.SetStateAction<boolean>>;
  handleChange: (val: ShippingMethodCheckoutType) => void;
  handleChecked?: (val: boolean) => void;
  defaultChecked?: boolean;
};

export default function CardShippingCheckout({
  carts,
  dataRates,
  disabled,
  hasNoPinpoint,
  isLoading,
  setIsCod,
  handleChange,
  handleChecked,
  defaultChecked,
}: CardShippingCheckoutType) {
  const modalShipping = useModal<FormSelectShippingType>();
  const shippingEta = carts?.shipping?.service?.label?.split(' - ');
  const eta = {
    from: shippingEta?.[0],
    to: shippingEta?.[1],
  };

  const handleSelectShipping = (data: ShippingMethodCheckoutType) => {
    modalShipping.close();
    setIsCod(data?.service?.is_cod_supported as boolean);
    modalShipping.setData({
      shippingMethodId: data?.courir_category_id as string,
      shippingServiceId: data?.service?.id as string,
    });

    handleChange(data);
  };

  const { data: locationData } = useLocationMultiOrigin();

  const { multiOrigin } = useCookies();
  const selectedLocationId = Cookies.get('_location_id');
  const hasLocations = Array.isArray(locationData) && locationData.length > 0;
  const defaultLocation = hasLocations ? locationData.find((loc) => loc.is_default) : null;

  const selectedLocation = hasLocations
    ? locationData.find((location) => location.location_id === selectedLocationId) ||
      defaultLocation
    : null;
  return (
    <>
      <div className='border-b pb-6'>
        <div className='flx flex-col'>
          <div className='flex items-center justify-between pb-1'>
            <div className='flex items-center gap-x-2'>
              <TruckIcon className='mt-0.5 h-5 w-5' />
              <h2 className='text-lg md:text-xl'>Shipping</h2>
            </div>
            <div className='flex w-full items-center justify-end gap-3'>
              <button
                type='button'
                className={cn('flex items-center text-sm', disabled && 'text-gray-500')}
                onClick={() => modalShipping.open()}
                disabled={disabled}
              >
                View all
                <ChevronRight className='mt-0.5 h-5 w-5' />
              </button>
            </div>
          </div>
          {multiOrigin && (
            <div className='flex items-start justify-start gap-x-2 pb-2'>
              <div className='flex items-start justify-start gap-x-2 pb-2'>
                <div>
                  <h2 className='text-sm font-normal'>From</h2>
                </div>
              </div>

              <div className='flex flex-col items-start justify-start text-slate-600'>
                {selectedLocation ? (
                  <>
                    <h1 className='text-sm '>{selectedLocation.location_name}</h1>
                    <span className='text-md'>{selectedLocation.location_address}</span>
                  </>
                ) : (
                  <span className='text-md text-gray-500'>No location found</span>
                )}
              </div>
            </div>
          )}
        </div>

        <div className='flex flex-col'>
          {isLoading ? (
            <div className='flex flex-col gap-2 p-2'>
              <Skeleton className='h-6 w-full rounded-sm bg-gray-200' />
              <Skeleton className='h-6 w-full rounded-sm bg-gray-200' />
            </div>
          ) : (
            <div className='my-4 flex items-center justify-between'>
              {carts?.shipping?.service ? (
                <>
                  <div className='flex items-center gap-4 text-sm'>
                    {carts?.shipping?.courier_logo && (
                      <Image
                        src={carts?.shipping?.courier_logo as string}
                        width={50}
                        height={50}
                        alt='Logo courier'
                      />
                    )}
                    <div className='flex flex-col gap-1'>
                      {carts?.shipping?.service?.name as string}
                      <span className='text-xs text-muted-foreground'>
                        Estimate:{' '}
                        {(carts?.shipping?.service?.label as string) &&
                          formatEstimateDelivery(eta.from as string, eta.to as string)}
                      </span>
                    </div>
                  </div>
                </>
              ) : (
                <div className='w-full rounded-md bg-gray-100 p-4'>
                  <span
                    className={cn(
                      'text-sm font-bold',
                      disabled ? 'text-gray-500' : 'text-gray-400'
                    )}
                  >
                    (
                    {disabled
                      ? 'Please complete your address details to view available shipping methods'
                      : 'Choose shipping method'}
                    )
                  </span>
                </div>
              )}

              {carts?.shipping_cost !== 0 && (
                <div>
                  <p className={cn('c text-sm text-muted-foreground')}>
                    {formatIDR(carts?.shipping_cost || 0)}
                  </p>
                </div>
              )}
            </div>
          )}
          {(carts?.shipping_insurance as number) > 0 && (
            <>
              <Separator className='my-0' />
              <div className='flex items-center space-x-2 p-3'>
                <Checkbox
                  id='is_include_insurance'
                  name='is_include_insurance'
                  className='rounded transition-all duration-300 ease-in-out'
                  onCheckedChange={handleChecked}
                  disabled={isLoading}
                  checked={defaultChecked}
                />
                <Label
                  htmlFor='is_include_insurance'
                  className='inline-flex items-center gap-1 text-xs text-gray-500'
                >
                  <ShieldCheck className='h-4 w-4' />
                  Shipping Insurance
                </Label>
              </div>
            </>
          )}
        </div>
      </div>
      <ModalShippingCheckout
        openModal={modalShipping.isShow}
        setOpenModal={modalShipping.setIsShow}
        dataRates={dataRates}
        onSelect={handleSelectShipping}
        disabled={hasNoPinpoint}
      />
    </>
  );
}
